// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/OrganizationSetup.jsx

import React from 'react';
import MissionCard from './shared/MissionCard';
import Select from '../../design-system/components/forms/Select';

const fieldHelp = {
  organization_name: 'Prefilled from your OASYS workspace. Correct it here if needed; saved changes update the governed organization record.',
  industry: 'Helps OASYS recommend the right frameworks and compliance operating model.',
  employee_count: 'Calibrates readiness, evidence coverage, and expected operational complexity.',
  audit_timeline: 'Prioritizes quick wins, evidence freshness, and executive briefing urgency.',
};

export default function OrganizationSetup({
  organization = {},
  onChange,
  onSubmit,
  saving = false,
}) {
  const update = (field, value) => onChange?.({ ...organization, [field]: value });
  const organizationName = String(organization.organization_name || '').trim();
  const canSave = Boolean(organizationName) && !saving;

  return (
    <div className="mission-step-shell">
      <MissionCard
        eyebrow="Phase 1 · Organization Foundation"
        title="Teach OASYS how your organization operates"
        description="This creates the foundation for framework guidance, connector coverage, readiness scoring, and your first executive mission brief."
      >
        <div className="mission-form-grid">
          <label className="mission-field">
            <span>Organization name</span>
            <input
              value={organization.organization_name || ''}
              onChange={(e) => update('organization_name', e.target.value)}
              placeholder="Acme Inc."
              maxLength={200}
              autoComplete="organization"
              disabled={saving}
            />
            <small>{fieldHelp.organization_name}</small>
          </label>

          <label className="mission-field">
            <span>Industry</span>
            <Select
              aria-label="Industry"
              value={organization.industry || ''}
              onChange={(e) => update('industry', e.target.value)}
              disabled={saving}
            >
              <option value="">Select industry</option>
              <option value="Technology">Technology</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Financial Services">Financial Services</option>
              <option value="Retail">Retail</option>
              <option value="Manufacturing">Manufacturing</option>
            </Select>
            <small>{fieldHelp.industry}</small>
          </label>

          <label className="mission-field">
            <span>Employee count</span>
            <Select
              aria-label="Employee count"
              value={organization.employee_count || ''}
              onChange={(e) => update('employee_count', e.target.value)}
              disabled={saving}
            >
              <option value="">Select range</option>
              <option value="1-50">1–50</option>
              <option value="51-200">51–200</option>
              <option value="201-1000">201–1,000</option>
              <option value="1000+">1,000+</option>
            </Select>
            <small>{fieldHelp.employee_count}</small>
          </label>

          <label className="mission-field">
            <span>Audit timeline</span>
            <Select
              aria-label="Audit timeline"
              value={organization.audit_timeline || ''}
              onChange={(e) => update('audit_timeline', e.target.value)}
              disabled={saving}
            >
              <option value="">Select timeline</option>
              <option value="30 days">30 days</option>
              <option value="90 days">90 days</option>
              <option value="6 months">6 months</option>
              <option value="No active audit">No active audit</option>
            </Select>
            <small>{fieldHelp.audit_timeline}</small>
          </label>
        </div>

        <div className="mission-form-section">
          <h4>Critical systems</h4>
          <p>These systems shape connector recommendations and operational readiness scoring.</p>
          <div className="mission-chip-picker">
            {['AWS', 'Google Workspace', 'Okta', 'GitHub', 'Cloudflare'].map((system) => {
              const selected = (organization.critical_systems || []).includes(system);
              return (
                <button
                  type="button"
                  className={`mission-choice-chip${selected ? ' mission-choice-chip-selected' : ''}`}
                  key={system}
                  disabled={saving}
                  onClick={() => {
                    const current = organization.critical_systems || [];
                    update('critical_systems', selected ? current.filter((item) => item !== system) : [...current, system]);
                  }}
                >
                  {system}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mission-step-actions">
          <button
            type="button"
            className="mission-primary-button"
            onClick={onSubmit}
            disabled={!canSave}
          >
            {saving ? 'Saving Organization Foundation…' : 'Save Organization Foundation'}
          </button>
        </div>
      </MissionCard>
    </div>
  );
}
